// React Dependencies
import React, { useRef, useState, useMemo, useReducer } from 'react';

// External Dependencies
import moment from 'moment';
import PropTypes from 'prop-types';
import MaterialTable from 'material-table';
import { connect } from 'react-redux';
import { Edit as EditIcon, Delete as DeleteIcon } from '@material-ui/icons';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import {
  Button, IconButton, Dialog, DialogTitle, Typography, DialogActions,
  Tooltip, Select, MenuItem, DialogContent, TextField,
} from '@material-ui/core';

// Internal Dependencies
import OverLoading from '../../global/OverLoading';
import apiService from '../../../services.js/apiService';
import Colours from '../../../styles/colours';
import { updateModalContent as updateModalAction, toggleModal as toggleModalAction } from '../../../actions/modal';
import { Status } from '../../../common/constants';
import { IsEmpty } from '../../../common/checks';

const defaultCreationModalState = { open: false, name: '' };

const LuckyDraws = ({ match, updateModalContent, toggleModal }) => {
  const pageId = match.params.id;
  const { url: currentUrl } = match;

  const tableRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [creationModal, setCreationModal] = useReducer((state, action) => ({ ...state, ...action }), defaultCreationModalState);

  const fetchTableData = (query) => async (resolve) => {
    try {
      setLoading(true);
      query.pageNo = query.page;
      const { page } = query;
      const queryFilters = Object.fromEntries(query.filters.map((filters) => [filters.column.field, filters.value]));
      const result = await apiService.getLuckyDraws(pageId, { ...query, ...queryFilters });

      resolve({ data: result.rows, page, totalCount: result.totalCount });
    } catch (err) {
      toast.error(err.message);
      resolve({ data: [], page: 0, totalCount: 0 });
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    try {
      setLoading(true);
      await apiService.createLuckyDraw(pageId, { name: creationModal.name });
      setCreationModal(defaultCreationModalState);
      tableRef.current.onQueryChange(); // updates table data
      toast.success('Lucky draw created successfully.');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteModal = (luckyDrawId) => {
    updateModalContent({
      title:        'Are you sure?',
      content:      'All data related to this lucky draw will be deleted!',
      actionButton: {
        actionText: 'Yes, Delete',
        actionType: 'danger',
        action:     async () => {
          try {
            setLoading(true);
            await apiService.deleteLuckyDraw(pageId, luckyDrawId);
            tableRef.current.onQueryChange();
            toast.success('Successfully deleted lucky draw.');
          } catch (err) {
            toast.error(err.message);
          } finally {
            setLoading(false);
          }
        },
      },
      cancelText:       'No',
      closeAfterAction: true,
    });
    toggleModal();
  };

  const columns = useMemo(() => [
    {
      title:     'ID',
      filtering: true,
      editable:  'never',
      field:     'id',
      cellStyle: { width: '10%' },
    },
    {
      title:     'Name',
      filtering: true,
      editable:  'never',
      field:     'name',
      cellStyle: { minWidth: 135 },
      render:    (luckyDraw) => (
        <Link target="_blank" to={`/admin/page/${luckyDraw.pageId}/luckyDraws/${luckyDraw.id}`}>
          {luckyDraw.name}
        </Link>
      ),
    },
    {
      title:           'Status',
      filtering:       true,
      editable:        'never',
      field:           'status',
      cellStyle:       { width: '10%' },
      filterComponent: (props) => (
        <Select
          className="w-100"
          // eslint-disable-next-line react/prop-types
          onChange={(e) => props.onFilterChanged(props.columnDef.tableData.id, e.target.value)}
        >
          <MenuItem value="">-</MenuItem>
          <MenuItem value={Status.Draft}>{Status.Draft}</MenuItem>
          <MenuItem value={Status.Live}>{Status.Live}</MenuItem>
          <MenuItem value={Status.Deactivated}>{Status.Deactivated}</MenuItem>
        </Select>
      ),
    },
    {
      title:           'Start Date',
      filtering:       true,
      editable:        'never',
      field:           'startDate',
      cellStyle:       { width: '10%' },
      // eslint-disable-next-line react/prop-types
      render:          ({ startDate }) => (!IsEmpty(startDate) && <Typography>{moment(startDate).utcOffset('+08').format('DD MMM YYYY')}</Typography>),
      filterComponent: (props) => (
        <TextField
          type="date"
          // eslint-disable-next-line react/prop-types
          onChange={({ target: { value } }) => props.onFilterChanged(props.columnDef.tableData.id, value)}
          InputLabelProps={{
            shrink: true,
          }}
        />
      ),
    },
    {
      title:           'End Date',
      filtering:       true,
      editable:        'never',
      field:           'endDate',
      cellStyle:       { width: '10%' },
      // eslint-disable-next-line react/prop-types
      render:          ({ endDate }) => (!IsEmpty(endDate) && <Typography>{moment(endDate).utcOffset('+08').format('DD MMM YYYY')}</Typography>),
      filterComponent: (props) => (
        <TextField
          type="date"
          // eslint-disable-next-line react/prop-types
          onChange={({ target: { value } }) => props.onFilterChanged(props.columnDef.tableData.id, value)}
          InputLabelProps={{
            shrink: true,
          }}
        />
      ),
    },
    {
      title:     'Last Updated by',
      filtering: true,
      editable:  'never',
      field:     'lastUpdatedBy',
      cellStyle: { width: '10%' },
    },
    {
      title:           'Updated on',
      filtering:       true,
      editable:        'never',
      field:           'updatedAt',
      cellStyle:       { width: '10%' },
      // eslint-disable-next-line react/prop-types
      render:          ({ updatedAt }) => (!IsEmpty(updatedAt) && <Typography>{moment(updatedAt).format('DD MMM YYYY')}</Typography>),
      filterComponent: (props) => (
        <TextField
          type="date"
          // eslint-disable-next-line react/prop-types
          onChange={({ target: { value } }) => props.onFilterChanged(props.columnDef.tableData.id, !IsEmpty(value) ? moment(value).format() : null)}
          InputLabelProps={{
            shrink: true,
          }}
        />
      ),
    },
    {
      title:     'Actions',
      filtering: false,
      editable:  'never',
      cellStyle: { width: '10%' },
      render:    (rowData) => (
        <div className="w-100 d-flex" style={{ gap: '0.8rem' }}>
          <Tooltip title="Edit">
            <IconButton size="medium" onClick={() => window.open(`${currentUrl}/${rowData.id}`, '_blank')}>
              <EditIcon htmlColor={Colours.Black} />
            </IconButton>
          </Tooltip>
          {rowData.status === Status.Draft && (
            <Tooltip title="Delete">
              <IconButton size="medium" onClick={() => handleDeleteModal(rowData.id)}>
                <DeleteIcon htmlColor={Colours.Black} />
              </IconButton>
            </Tooltip>
          )}
        </div>
      ),
    },
  ], []);

  return (
    <>
      {loading && <OverLoading />}

      <div className="row" style={{ backgroundColor: '#fff', marginLeft: 1 }}>
        <div className="col-12 d-flex align-items-center" style={{ padding: '5px 15px' }}>
          <Typography className="mr-3" style={{ fontSize: '2rem', fontWeight: 'bold' }}>Lucky Draws</Typography>
          <Button
            onClick={() => setCreationModal({ open: true })}
            variant="contained"
            color="primary"
            size="medium"
          >
            + Add
          </Button>
        </div>
        <div className="col-12 card-left">
          <MaterialTable
            tableRef={tableRef}
            columns={columns}
            isLoading={loading}
            components={{ Toolbar: () => null }}
            options={{ filtering: true, search: false, debounceInterval: 500, emptyRowsWhenPaging: false }}
            data={(query) => new Promise(fetchTableData(query))}
            localization={{ body: { emptyDataSourceMessage: 'No lucky draws to display' } }}
          />
        </div>
      </div>

      <Dialog open={creationModal.open} onClose={() => setCreationModal(defaultCreationModalState)} fullWidth>
        <DialogTitle className="px-4 pt-4">
          <Typography variant="h6" style={{ fontWeight: 'bold' }}>
            Create Lucky Draw
          </Typography>
        </DialogTitle>
        <DialogContent className="px-4 pb-4">
          <Typography variant="subtitle1">Name</Typography>
          <TextField
            variant="outlined"
            className="w-100"
            onChange={({ target }) => setCreationModal({ name: target.value })}
            value={creationModal.name}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreationModal({ open: false })} color="primary">Cancel</Button>
          <Button onClick={handleAdd} color="primary">Add</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

LuckyDraws.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }),
    url: PropTypes.string.isRequired,
  }).isRequired,
  updateModalContent: PropTypes.func.isRequired,
  toggleModal:        PropTypes.func.isRequired,
};

export default connect(null, { toggleModal: toggleModalAction, updateModalContent: updateModalAction })(LuckyDraws);
